import React, { FC, useMemo } from 'react';
import { Pill, PillProps, Button } from '@clearcover/atlas';
import styles from './styles.module.css';
import {
  TermStatusDetailType,
  PolicyTermInfoType,
  EffectivePeriodType,
  TermStatusType,
  GetPolicyDraftResponseType,
} from '@clearcover/core-app-types';
import {
  formatDateShortWithAt,
  formatDateTimeShortWithAbbreviatedTimezone,
} from 'Shared/utils';
import { useHistory } from 'react-router-dom';
import { History } from 'history';

export const COLOR_MAP: Record<
  TermStatusType | 'loading' | 'renewal',
  PillProps['variant']
> = {
  active: 'success',
  pending: 'warn',
  renewal: 'info',
  expired: 'danger',
  cancelled: 'danger',
  loading: 'loading',
};

type SubStatusDisplayDetails = {
  status: string;
  label: string;
  text: string;
  type: PillProps['variant'];
  button?: 'makePayment' | 'editRenewal' | 'viewRenewal';
};

const createSubStatusDisplayDetails = (
  status: TermStatusDetailType,
  effectivePeriod: { startsAt: string; endsAt: string },
  termInfo: PolicyTermInfoType,
  timeZone?: string,
  appliedCancellationEffectiveDate?: string
): SubStatusDisplayDetails | null => {
  switch (status) {
    case 'pending_renewal':
      return {
        status,
        label: 'Pending - Renewal Term',
        text: `Policy is pending renewal effective on ${formatDateShortWithAt(
          termInfo.pendingRenewalEffectiveDate || '',
          timeZone
        )}`,
        type: 'info',
        button: 'editRenewal',
      };
    case 'future_renewal':
      return {
        status,
        label: 'Future Renewal',
        text: `Policy will be renewed effective on ${formatDateShortWithAt(
          termInfo.appliedRenewalEffectiveDate || '',
          timeZone
        )}`,
        type: 'info',
        button: 'viewRenewal',
      };
    case 'pending_cancellation_other':
      return {
        status,
        label: 'Pending Cancellation',
        text: `This policy is pending cancellation and will be cancelled by ${formatDateShortWithAt(
          termInfo.cancellationEffectiveDate || '',
          timeZone
        )}`,
        type: 'danger',
      };
    case 'pending_cancellation_non_payment':
      return {
        status,
        label: 'Pending Cancellation',
        text: `This policy is pending cancellation due to an overdue balance. Please make a payment by ${formatDateShortWithAt(
          termInfo.cancellationEffectiveDate || '',
          timeZone
        )} to prevent cancellation.`,
        type: 'danger',
      };

    case 'future_effective_date':
      return {
        status,
        label: 'Future Effective',
        text: `Policy will be effective on ${formatDateShortWithAt(
          termInfo.futureEffectiveDate || '',
          timeZone
        )}`,
        type: 'info',
      };
    case 'non_renewed_by_insured':
    case 'non_renewed_by_insurer':
      return {
        status,
        label: 'Non-renewal',
        text: `Policy will be in effect until ${formatDateShortWithAt(
          effectivePeriod.endsAt,
          timeZone
        )} but will not renew`,
        type: 'info',
      };
    case 'cancelled_by_insurer':
    case 'cancelled_by_insured':
      if (!appliedCancellationEffectiveDate) return null;

      const isFutureCancellation =
        new Date(appliedCancellationEffectiveDate) > new Date();

      return isFutureCancellation
        ? {
            status,
            label: 'Pending Cancellation',
            text: `This policy is pending cancellation with coverage until ${formatDateTimeShortWithAbbreviatedTimezone(
              appliedCancellationEffectiveDate,
              timeZone
            )}.`,
            type: 'danger',
          }
        : {
            status,
            label: 'Cancelled',
            text: `This policy was cancelled on ${formatDateTimeShortWithAbbreviatedTimezone(
              appliedCancellationEffectiveDate,
              timeZone
            )}.`,
            type: 'danger',
          };
    default:
      return null;
  }
};
export const PolicyStatus: FC<{
  status?: PolicyTermInfoType['termStatus'] | 'renewal';
}> = ({ status }) => {
  const termLabel = status === 'renewal' ? 'Pending - Renewal Term' : status;

  return (
    <div data-testid="status" className={styles.status}>
      Policy status:
      <Pill
        className={styles.statusPill}
        variant={COLOR_MAP[status ?? 'loading']}
        data-testid="policy-status-value"
      >
        {termLabel ?? 'Active'}
      </Pill>
    </div>
  );
};

export type PaymentStatusType = {
  billingInfo: {
    overdue: number;
    dateBelowEquityThreshold?: string;
  };
};

function shouldDisplayPastDueBanner(
  termInfo?: PolicyTermInfoType,
  billingInfoResponse?: PaymentStatusType
): boolean {
  return (
    // Not cancelled or pending-cancellation
    termInfo?.termStatus === 'active' &&
    !termInfo.termStatusDetailsList.some((status) =>
      [
        'pending_cancellation_other',
        'pending_cancellation_non_payment',
      ].includes(status)
    ) &&
    // Has equity threshold date
    !!billingInfoResponse?.billingInfo.dateBelowEquityThreshold &&
    // Is overdue
    billingInfoResponse.billingInfo.overdue > 0
  );
}

const PolicySubStatus: FC<{
  policyId: string;
  termInfo: PolicyTermInfoType;
  renewalDraftId?: string;
  status: string;
  subStatusDisplayDetails: SubStatusDisplayDetails;
  history: History;
}> = ({
  policyId,
  termInfo,
  renewalDraftId,
  status,
  subStatusDisplayDetails: { label, text, type, button },
  history,
}) => (
  <div
    className={styles.subStatus}
    key={status}
    data-testid={`${status}-banner`}
  >
    <Pill
      variant={type}
      className={styles.subStatusPill}
      data-testid={`${status}-banner-pill`}
    >
      {label}
    </Pill>
    <span data-testid={`${status}-banner-text`}>{text}</span>
    {button === 'makePayment' && (
      <Button
        className={styles.subStatusButton}
        variant={'secondary'}
        link
        size={'sm'}
      >
        Make Payment
      </Button>
    )}

    {button === 'editRenewal' && (
      <Button
        className={styles.subStatusButton}
        data-testid={`${status}-view-renewal`}
        variant={'secondary'}
        size={'sm'}
        onClick={() => {
          history.push(`/policies/${policyId}/draft/${renewalDraftId}`);
        }}
      >
        View Renewal Term
      </Button>
    )}
    {button === 'viewRenewal' && (
      <Button
        className={styles.subStatusButton}
        data-testid={`${status}-view-renewal`}
        variant={'secondary'}
        size={'sm'}
        onClick={() => {
          history.push(`/policies/${policyId}/term/${termInfo.termNumber + 1}`);
        }}
      >
        View Renewal Term
      </Button>
    )}
  </div>
);

export const PolicySubStatusSection: FC<{
  policyId: string;
  termInfo: PolicyTermInfoType;
  effectivePeriod: EffectivePeriodType;
  draftType?: GetPolicyDraftResponseType['policyDraft']['draftType'];
  timeZone?: string;
  renewalDraftId?: string;
  billingInfoResponse?: PaymentStatusType;
  ignoreBanners?: Array<TermStatusDetailType>;
}> = ({
  policyId,
  termInfo,
  effectivePeriod,
  timeZone,
  renewalDraftId,
  draftType,
  billingInfoResponse,
  ignoreBanners,
}) => {
  const history = useHistory();
  const subStatusDisplayDetails = useMemo(() => {
    if (['renewal', 'renewal_quote'].includes(draftType ?? '')) {
      return [];
    }

    const subStatusDisplayDetails = (termInfo?.termStatusDetailsList ?? [])
      .map((status) => {
        if (ignoreBanners?.includes(status)) return null;

        const subStatusDisplayDetails = createSubStatusDisplayDetails(
          status,
          effectivePeriod,
          termInfo,
          timeZone,
          termInfo?.appliedCancellationEffectiveDate
        );
        if (subStatusDisplayDetails === null) return null;
        return subStatusDisplayDetails;
      })
      .filter((it) => it !== null) as Array<SubStatusDisplayDetails>;

    if (shouldDisplayPastDueBanner(termInfo, billingInfoResponse)) {
      subStatusDisplayDetails.push({
        status: 'payment_past_due',
        label: 'Past Due / Missed Payment',
        text: `The policy payment is past due because of a missed payment. To avoid disruption of coverage, please make the payment by ${formatDateShortWithAt(
          billingInfoResponse?.billingInfo.dateBelowEquityThreshold || '',
          timeZone
        )}.`,
        type: 'danger',
      });
    }

    return subStatusDisplayDetails;
  }, [
    billingInfoResponse,
    draftType,
    effectivePeriod,
    termInfo,
    timeZone,
    ignoreBanners,
  ]);

  if (subStatusDisplayDetails.length === 0) return null;

  return (
    <div className={styles.subStatusSection}>
      {subStatusDisplayDetails.map((subStatusDisplayDetails, i) => {
        return (
          <PolicySubStatus
            key={i}
            policyId={policyId}
            termInfo={termInfo}
            renewalDraftId={renewalDraftId}
            status={subStatusDisplayDetails.status}
            subStatusDisplayDetails={subStatusDisplayDetails}
            history={history}
          />
        );
      })}
    </div>
  );
};
